import { type JSX, useEffect, useId, useState } from 'react';

import {
  KATEGORIEN,
  KATEGORIENAME,
  STATUSNAME,
  VORGANGSSTATUS,
  zuweisbare as ladeZuweisbare,
  type Bearbeitungsansicht,
  type Kategorie,
  type Vorgangsstatus,
  type Zielstand,
  type Zuweisbare,
  type Zustaendigkeit,
} from '../api/support';
import { alsZeitpunkt } from '../datum';
import { markenklasse } from '../marken';
import { alsEingabezeit, ausEingabezeit } from './bearbeitungshilfe';
import { dauer, Faelligkeit, Kategorieangabe, Zustaendig } from './Listenangaben';
import { statusmarke } from './Zuordnung';

/**
 * DIE LEISTE ÜBER DEM VORGANG (Auftrag CRM-178): Status, Zuständig, Fällig,
 * Kategorie und die Ziele. Wer bearbeiten darf und nicht am Telefon sitzt,
 * ändert hier; alle anderen lesen dieselben Angaben.
 */

/** Die Handgriffe der Leiste; jeder lädt danach die Ansicht neu (der Aufrufer). */
export type Leistenhandlungen = {
  readonly status: (status: Vorgangsstatus) => void;
  readonly zuweisen: (an: { readonly benutzer: string | null; readonly rolle: string | null }) => void;
  readonly faellig: (am: string | null) => void;
  readonly kategorie: (kategorie: Kategorie | null) => void;
  readonly uebernehmen: () => void;
};

export function Bearbeitungsleiste({
  ansicht: a,
  bearbeitbar,
  ich,
  laeuft,
  tue,
}: {
  readonly ansicht: Bearbeitungsansicht;
  readonly bearbeitbar: boolean;
  /** Der angemeldete Benutzer — „Übernehmen" fehlt, wenn der Vorgang schon meiner ist. */
  readonly ich: string | undefined;
  readonly laeuft: boolean;
  readonly tue: Leistenhandlungen;
}): JSX.Element {
  const offen = a.status !== 'geloest' && a.status !== 'geschlossen';
  const kennung = useId();

  return (
    <section className="vorgang__leiste" aria-label="Bearbeitung">
      <dl className="vorgang__angaben">
        <dt>{bearbeitbar ? <label htmlFor={`${kennung}-status`}>Status</label> : 'Status'}</dt>
        <dd>
          {bearbeitbar ? (
            <select
              id={`${kennung}-status`}
              value={a.status}
              disabled={laeuft}
              onChange={(e) => {
                tue.status(e.target.value as Vorgangsstatus);
              }}
            >
              {VORGANGSSTATUS.map((s) => (
                <option key={s} value={s}>
                  {STATUSNAME[s]}
                </option>
              ))}
            </select>
          ) : (
            statusmarke(a.status)
          )}
        </dd>

        <dt>{bearbeitbar ? <label htmlFor={`${kennung}-zustaendig`}>Zuständig</label> : 'Zuständig'}</dt>
        <dd>
          {bearbeitbar ? (
            <Zustaendigwahl
              id={`${kennung}-zustaendig`}
              zustaendigkeit={a.zustaendigkeit}
              laeuft={laeuft}
              beiWahl={tue.zuweisen}
            />
          ) : (
            <Zustaendig zustaendigkeit={a.zustaendigkeit} />
          )}
          {/* SUP-26: Die Vertretung steht auch beim Bearbeiten dabei. */}
          {bearbeitbar && a.zustaendigkeit.vertretenDurch !== null && (
            <>
              <br />
              <span className={markenklasse('aufmerksamkeit')}>
                abwesend, vertreten durch {a.zustaendigkeit.vertreterName ?? 'die Vertretung'}
              </span>
            </>
          )}
          {/* SUP-18: sich selbst zuweisen, ein Griff. */}
          {bearbeitbar && (ich === undefined ? a.zustaendigkeit.benutzer === null : a.zustaendigkeit.benutzer !== ich) && (
            <>
              {' '}
              <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={tue.uebernehmen}>
                Übernehmen
              </button>
            </>
          )}
        </dd>

        <dt>{bearbeitbar ? <label htmlFor={`${kennung}-faellig`}>Fällig</label> : 'Fällig'}</dt>
        <dd>
          {bearbeitbar ? (
            <Faelligwahl id={`${kennung}-faellig`} am={a.faelligAm} laeuft={laeuft} beiWahl={tue.faellig} />
          ) : (
            <Faelligkeit am={a.faelligAm} offen={offen} />
          )}
        </dd>

        <dt>{bearbeitbar ? <label htmlFor={`${kennung}-kategorie`}>Kategorie</label> : 'Kategorie'}</dt>
        <dd>
          {bearbeitbar ? (
            <>
              <select
                id={`${kennung}-kategorie`}
                value={a.kategorie ?? ''}
                disabled={laeuft}
                onChange={(e) => {
                  tue.kategorie(e.target.value === '' ? null : (e.target.value as Kategorie));
                }}
              >
                <option value="">ohne Kategorie</option>
                {KATEGORIEN.map((k) => (
                  <option key={k} value={k}>
                    {KATEGORIENAME[k]}
                  </option>
                ))}
              </select>
              {/* SUP-14: Der Vorschlag der Regel gilt erst, wenn ihn jemand übernimmt. */}
              {a.kategorie === null && a.kategorieVorschlag !== null && (
                <>
                  <br />
                  <em className="hilfe">Vorschlag: {KATEGORIENAME[a.kategorieVorschlag]}</em>{' '}
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    disabled={laeuft}
                    onClick={() => {
                      if (a.kategorieVorschlag !== null) tue.kategorie(a.kategorieVorschlag);
                    }}
                  >
                    Vorschlag übernehmen
                  </button>
                </>
              )}
            </>
          ) : (
            <Kategorieangabe kategorie={a.kategorie} vorschlag={a.kategorieVorschlag} />
          )}
        </dd>

        {/* SUP-25: beide Ziele mit Restzeit; überschritten rot. */}
        {a.reaktion !== null && (
          <>
            <dt>Reaktion</dt>
            <dd>
              <Zielangabe ziel={a.reaktion} />
            </dd>
          </>
        )}
        {a.loesung !== null && (
          <>
            <dt>Lösung</dt>
            <dd>
              <Zielangabe ziel={a.loesung} />
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}

/** SUP-25: erreicht, noch offen mit Restzeit oder überschritten (rot). */
export function Zielangabe({ ziel }: { readonly ziel: Zielstand }): JSX.Element {
  const bis = alsZeitpunkt(ziel.bis) ?? ziel.bis;
  if (ziel.erreichtAm !== null) {
    return (
      <span>
        <span className={markenklasse('abschluss')}>erreicht</span>{' '}
        <span className="hilfe">am {alsZeitpunkt(ziel.erreichtAm) ?? ziel.erreichtAm}, Ziel war {bis}</span>
      </span>
    );
  }
  const rest = ziel.restMinuten ?? Math.floor((Date.parse(ziel.bis) - Date.now()) / 60_000);
  if (ziel.ueberschritten || rest < 0) {
    return (
      <span className="vorgang__ziel--ueberschritten">
        <span className={markenklasse('sackgasse')}>überschritten</span> seit {dauer(rest, 'seit')} (Ziel {bis})
      </span>
    );
  }
  return (
    <span className={rest < 60 ? markenklasse('aufmerksamkeit') : undefined}>
      noch {dauer(rest, 'noch')} <span className="hilfe">bis {bis}</span>
    </span>
  );
}

/**
 * SUP-18: Person, Team oder niemand (Warteschlange). Abwesende stehen mit
 * dem Zusatz in der Liste (SUP-26) — wählbar bleiben sie, ihre Vertretung
 * sieht den Vorgang dann in „Meine".
 */
function Zustaendigwahl({
  id,
  zustaendigkeit: z,
  laeuft,
  beiWahl,
}: {
  readonly id: string;
  readonly zustaendigkeit: Zustaendigkeit;
  readonly laeuft: boolean;
  readonly beiWahl: Leistenhandlungen['zuweisen'];
}): JSX.Element {
  const [wahl, setzeWahl] = useState<Zuweisbare | null>(null);

  useEffect(() => {
    let gilt = true;
    ladeZuweisbare().then(
      (w) => {
        if (gilt) setzeWahl(w);
      },
      () => {
        // Ohne Auswahl bleibt die jetzige Zuständigkeit stehen; die Leiste zeigt sie trotzdem.
        if (gilt) setzeWahl({ benutzer: [], rollen: [] });
      },
    );
    return () => {
      gilt = false;
    };
  }, []);

  const wert = z.benutzer !== null ? `b:${z.benutzer}` : z.rolle !== null ? `r:${z.rolle}` : '';
  const personen = wahl?.benutzer ?? [];
  const rollen = wahl?.rollen ?? [];
  // Die jetzige Zuständigkeit bleibt wählbar, auch wenn sie nicht (mehr) in der Auswahl steht.
  const fehltPerson = z.benutzer !== null && !personen.some((p) => p.id === z.benutzer);
  const fehltRolle = z.rolle !== null && z.benutzer === null && !rollen.some((r) => r.kennung === z.rolle);

  return (
    <select
      id={id}
      value={wert}
      disabled={laeuft || wahl === null}
      onChange={(e) => {
        const v = e.target.value;
        if (v.startsWith('b:')) beiWahl({ benutzer: v.slice(2), rolle: null });
        else if (v.startsWith('r:')) beiWahl({ benutzer: null, rolle: v.slice(2) });
        else beiWahl({ benutzer: null, rolle: null });
      }}
    >
      <option value="">Warteschlange (niemand)</option>
      {(personen.length > 0 || fehltPerson) && (
        <optgroup label="Person">
          {fehltPerson && <option value={wert}>{z.benutzername ?? 'jetzige Person'}</option>}
          {personen.map((p) => (
            <option key={p.id} value={`b:${p.id}`}>
              {p.abwesend ? `${p.name} (abwesend)` : p.name}
            </option>
          ))}
        </optgroup>
      )}
      {(rollen.length > 0 || fehltRolle) && (
        <optgroup label="Team">
          {fehltRolle && <option value={wert}>{z.rollenname ?? 'jetziges Team'}</option>}
          {rollen.map((r) => (
            <option key={r.kennung} value={`r:${r.kennung}`}>
              {r.bezeichnung}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

/** SUP-17: die Fälligkeit; gesetzt wird beim Verlassen des Felds, nicht bei jedem Tastendruck. */
function Faelligwahl({
  id,
  am,
  laeuft,
  beiWahl,
}: {
  readonly id: string;
  readonly am: string | null;
  readonly laeuft: boolean;
  readonly beiWahl: (am: string | null) => void;
}): JSX.Element {
  const [wert, setzeWert] = useState(alsEingabezeit(am));

  useEffect(() => {
    setzeWert(alsEingabezeit(am));
  }, [am]);

  const uebernehmen = (): void => {
    const neu = ausEingabezeit(wert);
    if (neu === null && wert.trim() !== '') return;
    if (alsEingabezeit(neu) !== alsEingabezeit(am)) beiWahl(neu);
  };

  return (
    <>
      <input
        id={id}
        type="datetime-local"
        value={wert}
        disabled={laeuft}
        onChange={(e) => {
          setzeWert(e.target.value);
        }}
        onBlur={uebernehmen}
      />
      {am !== null && (
        <>
          {' '}
          <button
            type="button"
            className="knopf knopf--klein"
            disabled={laeuft}
            onClick={() => {
              beiWahl(null);
            }}
          >
            Ohne Fälligkeit
          </button>
        </>
      )}
    </>
  );
}
